import { Card, CardContent, CardHeader, CardTitle } from "@aurumiq/ui/components/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@aurumiq/ui/components/tabs";
import { type ColumnDef, createColumnHelper, DataTable } from "@aurumiq/ui/composite/data-table";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { dmyTime, relativeTime } from "@aurumiq/ui/lib/format";
import { useState } from "react";
import { JsonView } from "@/components/json-view";
import { QueryState } from "@/components/query-state";
import { errorMessage } from "@/lib/api";
import type { ActivityItem, TelemetryCrash, TelemetryEvent } from "@/lib/types";
import { useOrgActivity, useOrgTelemetry } from "./queries";

const ev = createColumnHelper<TelemetryEvent>();
const eventColumns = [
  ev.accessor("kind", {
    header: "Kind",
    cell: (c) => <span className="font-mono text-xs">{c.getValue()}</span>,
  }),
  ev.accessor("device_id", {
    header: "Device",
    cell: (c) => <span className="font-mono text-xs">{c.getValue()}</span>,
  }),
  ev.accessor("app_version", { header: "App", cell: (c) => c.getValue() ?? "—" }),
  ev.accessor("occurred_at", {
    header: "When",
    cell: (c) => <span title={dmyTime(c.getValue())}>{relativeTime(c.getValue())}</span>,
  }),
  ev.accessor("props", {
    header: "Props",
    cell: (c) => <JsonView value={c.getValue()} label="props" />,
  }),
] as ColumnDef<TelemetryEvent, unknown>[];

const cr = createColumnHelper<TelemetryCrash>();
const crashColumns = [
  cr.accessor("message", {
    header: "Message",
    cell: (c) => <span className="line-clamp-2 max-w-md font-mono text-xs">{c.getValue()}</span>,
  }),
  cr.accessor("group_hash", {
    header: "Group",
    cell: (c) => (
      <span className="font-mono text-xs" title={c.getValue()}>
        {c.getValue().slice(0, 8)}
      </span>
    ),
  }),
  cr.accessor("device_id", {
    header: "Device",
    cell: (c) => <span className="font-mono text-xs">{c.getValue()}</span>,
  }),
  cr.accessor("app_version", { header: "App", cell: (c) => c.getValue() ?? "—" }),
  cr.accessor("occurred_at", {
    header: "When",
    cell: (c) => <span title={dmyTime(c.getValue())}>{relativeTime(c.getValue())}</span>,
  }),
] as ColumnDef<TelemetryCrash, unknown>[];

const KIND_VARIANT = { license_event: "gold", audit: "default", scan: "ok" } as const;
const ac = createColumnHelper<ActivityItem>();
const activityColumns = [
  ac.accessor("at", {
    header: "When",
    cell: (c) => <span title={relativeTime(c.getValue())}>{dmyTime(c.getValue())}</span>,
  }),
  ac.accessor("kind", {
    header: "Kind",
    cell: (c) => (
      <StatusTag variant={KIND_VARIANT[c.getValue()] ?? "default"}>
        {c.getValue().replace("_", " ").toUpperCase()}
      </StatusTag>
    ),
  }),
  ac.accessor("event", {
    header: "Event",
    cell: (c) => <span className="font-mono text-xs">{c.getValue()}</span>,
  }),
  ac.accessor("actor", {
    header: "Actor",
    cell: (c) => c.getValue() ?? <span className="text-muted-foreground">system</span>,
  }),
  ac.accessor("meta", {
    header: "Meta",
    cell: (c) => <JsonView value={c.getValue()} label="meta" />,
  }),
] as ColumnDef<ActivityItem, unknown>[];

function ActivityFeed({ orgId }: { orgId: string }) {
  const [page, setPage] = useState(1);
  const activity = useOrgActivity(orgId, page);
  const total = activity.data?.total ?? 0;
  const pageSize = activity.data?.page_size ?? 25;
  return (
    <DataTable
      columns={activityColumns}
      data={activity.data?.items ?? []}
      isLoading={activity.isPending}
      getRowId={(a, i) => `${a.at}-${i}`}
      pageCount={Math.max(1, Math.ceil(total / pageSize))}
      pagination={{ pageIndex: page - 1, pageSize }}
      onPaginationChange={(p) => setPage(p.pageIndex + 1)}
      pageSizeOptions={[pageSize]}
      emptyMessage={
        activity.isError
          ? `Could not load activity: ${errorMessage(activity.error)}`
          : "No activity yet."
      }
    />
  );
}

export function TelemetryTab({ orgId }: { orgId: string }) {
  const telemetry = useOrgTelemetry(orgId);
  return (
    <Tabs defaultValue="events">
      <TabsList>
        <TabsTrigger value="events">Events</TabsTrigger>
        <TabsTrigger value="crashes">Crashes</TabsTrigger>
        <TabsTrigger value="activity">Activity feed</TabsTrigger>
      </TabsList>
      <TabsContent value="events">
        <Card>
          <CardHeader>
            <CardTitle>Recent events</CardTitle>
          </CardHeader>
          <CardContent>
            <QueryState query={telemetry}>
              {(t) => (
                <DataTable
                  columns={eventColumns}
                  data={t.events}
                  getRowId={(e) => e.event_id}
                  emptyMessage="No events received."
                />
              )}
            </QueryState>
          </CardContent>
        </Card>
      </TabsContent>
      <TabsContent value="crashes">
        <Card>
          <CardHeader>
            <CardTitle>Crashes</CardTitle>
          </CardHeader>
          <CardContent>
            <QueryState query={telemetry}>
              {(t) => (
                <DataTable
                  columns={crashColumns}
                  data={t.crashes}
                  getRowId={(c) => c.id}
                  emptyMessage="No crashes. Good."
                />
              )}
            </QueryState>
          </CardContent>
        </Card>
      </TabsContent>
      <TabsContent value="activity">
        <ActivityFeed orgId={orgId} />
      </TabsContent>
    </Tabs>
  );
}
